<!--
  BunnyImage — renders an <img> whose src is a render-time-resolved (and
  auto-renewed) Bunny signed URL.

  Pass an `asset-id` (asset-doc-backed: logos, episode thumbs) OR a raw `path`
  (avatars), plus a `fallback` (the legacy stored URL — used for pre-Bunny
  Firebase docs and while the fresh URL resolves). On a load error (a token
  that expired between resolve and render) it re-mints once.

  If the ONLY pointer is a `fallback` that is itself a stale Bunny signed URL
  (legacy docs that stored the signed URL instead of an assetId/path — these
  expire in ~1h and then 403), we recover the storage path from that URL and
  re-sign it fresh, so the image self-heals instead of latching broken.

  When there is no resolvable URL, the `placeholder` slot renders instead, so
  callers can keep their existing initials/icon fallbacks.
-->
<template>
  <img
    v-if="displaySrc"
    :src="displaySrc"
    :alt="alt"
    :class="imgClass"
    @error="onError"
  />
  <slot v-else name="placeholder" />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useBunnyUrl } from '@/composables/useBunnyUrl';
import { BUNNY_URL_INTENT } from '@/utils/bunnyConstants';

const props = defineProps({
  assetId: { type: String, default: null },
  path: { type: String, default: null },
  // Legacy stored URL (Firebase, or a not-yet-expired Bunny URL). Shown while
  // resolving and returned as-is when there is no assetId/path.
  fallback: { type: String, default: null },
  intent: { type: String, default: BUNNY_URL_INTENT.THUMBNAIL },
  shareToken: { type: String, default: null },
  alt: { type: String, default: '' },
  imgClass: { type: [String, Array, Object], default: null },
  // Optimizer width for thumbnail-intent images. Null = server default (400px,
  // right for cards). Set higher (e.g. 1200) where the image renders large.
  width: { type: Number, default: null },
});

// A stale Bunny signed URL is self-identifying (advanced-token auth: HS256-
// token + expires). If that's all we have, its pathname IS the storage path —
// hand it to the resolver as `path` so it re-signs fresh instead of rendering
// the expired (403-ing) URL. Firebase/other legacy URLs don't match, so they
// pass through untouched as the fallback.
function bunnyPathFromFallback(fallbackUrl) {
  if (!fallbackUrl || !/[?&]token=HS256/.test(fallbackUrl) || !/[?&]expires=/.test(fallbackUrl)) return null;
  try {
    return decodeURIComponent(new URL(fallbackUrl).pathname).replace(/^\/+/, '');
  } catch {
    return null;
  }
}

const { url, refresh } = useBunnyUrl(() => ({
  assetId: props.assetId,
  path: props.path || bunnyPathFromFallback(props.fallback),
  intent: props.intent,
  shareToken: props.shareToken,
  fallback: props.fallback,
  width: props.width,
}));

// `displayOverride` wins over the resolved `url` (drop to the raw legacy fallback
// after the Bunny URL fails); `giveUp` nulls the src so the placeholder shows once
// nothing is recoverable.
const displayOverride = ref(null);
const giveUp = ref(false);
const displaySrc = computed(() => (giveUp.value ? null : (displayOverride.value || url.value)));

// A new source (different asset/path) resets the error-recovery state.
watch(() => [props.assetId, props.path], () => {
  displayOverride.value = null;
  giveUp.value = false;
  retried = false;
  fellBack = false;
});

// Drop to the raw legacy fallback (e.g. a still-live Firebase thumbnail) if it's
// distinct and not itself a stale Bunny signed URL (which would just 403 again).
// Otherwise there's nothing recoverable → placeholder.
function fallBackToLegacy(failedSrc) {
  const fb = props.fallback;
  if (fb && fb !== failedSrc && !bunnyPathFromFallback(fb)) {
    fellBack = true;
    displayOverride.value = fb;
  } else {
    giveUp.value = true;
  }
}

let retried = false;
let fellBack = false;
async function onError() {
  const failedSrc = displaySrc.value;
  if (!retried) {
    // First failure: a token that may have expired between resolve and render —
    // re-mint once. If the re-mint yields the SAME url (a genuinely dead pointer,
    // not just a stale token), the <img> won't re-fire @error, so recover now
    // instead of waiting for a second error that never comes.
    retried = true;
    await refresh();
    if (displaySrc.value === failedSrc) fallBackToLegacy(failedSrc);
    return;
  }
  if (fellBack) { giveUp.value = true; return; } // fallback also failed → placeholder
  fallBackToLegacy(failedSrc);
}
</script>
